import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { Link, useNavigate } from 'react-router-dom';
import { useAccount } from '../../context/AccountContext';
import { GET_TOPICS_PAGE } from '../../graphql/queries';
import { useCopy } from '../../lib/copyToClipboard';
import { uploadAsset } from '../../lib/media/uploadAsset';
import { currentUrl, LibraryAsset, MediaUrlPicker } from '../content/MediaUrlField';
import { useAgentConnected } from './AgentStatus';
import { askInk } from './InkConfirm';
import { InkSkeleton } from './InkSkeleton';
import { showInkToast } from './InkToast';
import { ARTICLE_STATUS, ArticleBucket, articleBucket } from '../statusWords';
import { useTinycmsSites } from './sites';
import {
  Author,
  AUTHOR_PHOTO_ASSET,
  AuthorArticle,
  CREATE_TINYCMS_AUTHOR,
  DELETE_TINYCMS_AUTHOR,
  GET_TINYCMS_AUTHOR,
  GET_TINYCMS_AUTHORS,
  initials,
  profileChecks,
  SOCIALS,
  SocialKey,
  socialShown,
  UPDATE_TINYCMS_AUTHOR,
} from './authors';

/**
 * Content → Authors (Prototype v17, backlog 53b14acc): the site's authors on
 * the left — each with their role, how many articles credit them, and an amber
 * dot while their profile is missing something — and the chosen one on the
 * right: who they are, what the profile still needs (search engines weigh who
 * wrote an article), their details, an editor for every detail, and the
 * articles that credit them. People add (New author) and edit authors here;
 * agents do the same over MCP. Deleting one is here only, never an agent's
 * (owner, 2026-10-25), and only once no article credits them.
 *
 * UX review round 11: with no authors yet, the screen leads with what that
 * costs ("2,570 live articles have no byline") and asks the agent to find the
 * authors already named in the articles; adding one yourself is second. The
 * New author form says only the name is needed, and shows what's missing once
 * it's saved. "Writes about" is picked from the site's topics, and the photo
 * is uploaded or chosen from Media, not typed as an address.
 */

/** Copied for the agent from the empty Authors screen. */
export const FIND_AUTHORS_PROMPT =
  'Read the bylines in my published articles. Create an author for each person you find, with their name and role, and set them as the author on their articles. List anything you’re unsure about for me to check.';

/** Topic chips shown before "Show all". */
const TOPICS_SHOWN = 24;

const BIO_MAX = 1200;
/** What goes after each network's address prefix. */
const SOCIAL_PLACEHOLDER: Record<SocialKey, string> = {
  x: 'X username',
  linkedin: 'LinkedIn profile name',
  threads: 'Threads username',
  instagram: 'Instagram username',
  site: 'example.com',
};
const ARTICLES_SHOWN = 8;

type Draft = {
  name: string;
  role: string;
  bio: string;
  topics: string[];
  photoUrl: string;
  email: string;
  socials: Record<SocialKey, string>;
};

const EMPTY: Draft = {
  name: '',
  role: '',
  bio: '',
  topics: [],
  photoUrl: '',
  email: '',
  socials: Object.fromEntries(SOCIALS.map(([k]) => [k, ''])) as Record<SocialKey, string>,
};

/** What the server takes from a draft (null clears a detail). */
const inputOf = (draft: Draft) => ({
  name: draft.name,
  role: draft.role || null,
  bio: draft.bio || null,
  photoUrl: draft.photoUrl.trim() || null,
  topics: draft.topics,
  email: draft.email.trim() || null,
  socials: Object.fromEntries(SOCIALS.map(([k]) => [k, draft.socials[k].trim() || null])),
});

const draftOf = (a: Author): Draft => ({
  name: a.name,
  role: a.role || '',
  bio: a.bio || '',
  topics: [...(a.topics || [])],
  photoUrl: a.photoUrl || '',
  email: a.email || '',
  socials: Object.fromEntries(SOCIALS.map(([k]) => [k, a.socials?.[k] || ''])) as Record<
    SocialKey,
    string
  >,
});

const since = (iso?: string) =>
  iso
    ? `Author since ${new Date(iso).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`
    : '';

const STATUS_DOT: Record<ArticleBucket, string> = {
  live: 'var(--ink-green)',
  scheduled: 'var(--ink-accent)',
  review: 'var(--ink-amber)',
  draft: 'var(--ink-line3)',
};

/** A credited article's bucket, the same as Website › Articles shows for it. */
const bucketOf = (r: AuthorArticle) => articleBucket(r.awaitingApproval, r.status);

export function CmsAuthorsView({
  initial,
  onEditing,
}: {
  initial: string | null;
  /** The header hides New author while an author's details are being edited. */
  onEditing?: (editing: boolean) => void;
}) {
  const { accountId } = useAccount();
  const navigate = useNavigate();
  const { sites } = useTinycmsSites();
  const host = sites.find((s) => s.accountId === accountId)?.host || null;
  const { data, loading, refetch } = useQuery<{ tinycmsAuthors: Author[] }>(GET_TINYCMS_AUTHORS, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const authors = useMemo(() => data?.tinycmsAuthors || [], [data]);
  // The site's live articles and their topics: the empty screen's count, and
  // the topics "Writes about" is picked from.
  const { data: siteData } = useQuery<{
    topicsPage: { counts: { live: number }; categories: Array<{ name: string; count: number }> };
  }>(GET_TOPICS_PAGE, {
    variables: { accountId, offset: 0, limit: 1 },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const liveArticles = siteData?.topicsPage.counts.live ?? null;
  const topicOptions = useMemo(
    () => [
      ...new Set([
        ...(siteData?.topicsPage.categories || []).map((c) => c.name.trim()).filter(Boolean),
        ...authors.flatMap((a) => a.topics || []),
      ]),
    ],
    [siteData, authors]
  );
  const agentConnected = useAgentConnected();
  const { copy, state: copyState } = useCopy();
  const [sel, setSel] = useState<string | null>(initial);
  const creating = sel === 'new';
  const selected = creating ? null : authors.find((a) => a.id === sel) || authors[0] || null;
  useEffect(() => {
    if (initial) setSel(initial);
  }, [initial]);
  const open = (id: string | null) =>
    navigate(id ? `/content?view=authors&author=${id}` : '/content?view=authors', {
      replace: true,
    });

  const newAuthor = creating ? (
    <NewAuthor
      accountId={accountId!}
      topicOptions={topicOptions}
      onCancel={() => {
        setSel(null);
        open(null);
      }}
      onAdded={async (id) => {
        await refetch();
        setSel(id);
        open(id);
      }}
    />
  ) : null;

  if (!data && loading) return <InkSkeleton rows={4} label="Loading authors" />;
  if (!authors.length) {
    const copied = copyState('find');
    return (
      newAuthor || (
        <div className="au-empty">
          <div className="au-empty-title">No authors yet</div>
          {liveArticles ? (
            <div className="au-empty-impact">
              <span className="ink-dot" style={{ background: 'var(--ink-amber)' }} />
              {liveArticles.toLocaleString('en-US')} live{' '}
              {liveArticles === 1 ? 'article has' : 'articles have'} no byline
            </div>
          ) : null}
          <p>
            Bylines tell readers and search engines who wrote each article. Your agent can read the
            names already in your articles and set up an author for each.
          </p>
          <div className="au-empty-actions">
            <button
              type="button"
              className="ink-btn ink-btn-primary"
              onClick={async (e) => {
                if (await copy('find', FIND_AUTHORS_PROMPT, e))
                  showInkToast(
                    agentConnected === false
                      ? 'Prompt copied. Connect an agent in Account › AI Agents, then paste it there.'
                      : 'Prompt copied. Paste it into your agent.'
                  );
              }}
            >
              {copied === 'copied'
                ? 'Prompt copied'
                : copied === 'failed'
                  ? 'Couldn’t copy'
                  : 'Ask your agent to find authors in your articles'}
            </button>
            <button type="button" className="ink-btn" onClick={() => setSel('new')}>
              Add one yourself
            </button>
          </div>
          <p className="au-empty-note">Nothing goes live until you approve it.</p>
        </div>
      )
    );
  }

  return (
    <div className="au">
      <aside className="au-list" aria-label="Authors">
        <div className="au-list-label">AUTHORS</div>
        {authors.map((a) => {
          const missing = profileChecks(a).filter((c) => !c.ok).length;
          const n = a.articleCount || 0;
          return (
            <button
              key={a.id}
              type="button"
              className="au-row"
              data-on={selected?.id === a.id ? 'true' : undefined}
              onClick={() => {
                setSel(a.id);
                if (creating) open(a.id);
              }}
            >
              <Avatar author={a} size={30} />
              <span className="au-row-text">
                <span className="au-row-name">{a.name}</span>
                <span className="au-row-sub">
                  {[a.role, `${n} ${n === 1 ? 'article' : 'articles'}`].filter(Boolean).join(' · ')}
                </span>
              </span>
              {missing ? (
                <span
                  className="au-warn"
                  title={`${missing} profile ${missing === 1 ? 'detail' : 'details'} missing`}
                />
              ) : null}
            </button>
          );
        })}
        <p className="au-list-note">
          Add authors with New author, or ask your agent to. Pick an author in an article’s byline
          to credit them.
        </p>
      </aside>
      {newAuthor}
      {selected ? (
        <AuthorDetail
          key={selected.id}
          accountId={accountId!}
          author={selected}
          host={host}
          topicOptions={topicOptions}
          onEditing={onEditing}
          onSaved={() => void refetch()}
          onDeleted={async () => {
            await refetch();
            setSel(null);
            open(null);
          }}
          onOpenArticle={(id) => navigate(`/content/${id}`)}
        />
      ) : null}
    </div>
  );
}

function Avatar({ author, size }: { author: Pick<Author, 'name' | 'photoUrl'>; size: number }) {
  return author.photoUrl ? (
    <img className="au-avatar" src={author.photoUrl} alt="" style={{ width: size, height: size }} />
  ) : (
    <span
      className="au-avatar au-avatar-ini"
      aria-hidden="true"
      style={{ width: size, height: size, fontSize: size >= 48 ? 18 : 11 }}
    >
      {initials(author.name)}
    </span>
  );
}

function AuthorDetail({
  accountId,
  author,
  host,
  topicOptions,
  onEditing,
  onSaved,
  onDeleted,
  onOpenArticle,
}: {
  accountId: string;
  author: Author;
  host: string | null;
  topicOptions: string[];
  onEditing?: (editing: boolean) => void;
  onSaved: () => void;
  onDeleted: () => Promise<void>;
  onOpenArticle: (id: string) => void;
}) {
  const { data, refetch } = useQuery<{ tinycmsAuthor: Author & { articles: AuthorArticle[] } }>(
    GET_TINYCMS_AUTHOR,
    { variables: { accountId, id: author.id }, fetchPolicy: 'cache-and-network' }
  );
  const [save, { loading: saving }] = useMutation(UPDATE_TINYCMS_AUTHOR);
  const [remove, { loading: deleting }] = useMutation(DELETE_TINYCMS_AUTHOR);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [all, setAll] = useState(false);
  const a = data?.tinycmsAuthor || author;
  const articles = data?.tinycmsAuthor?.articles || [];
  const checks = profileChecks(a);
  const missing = checks.filter((c) => !c.ok).length;
  const live = a.liveCount || 0;
  const pageUrl = host ? `https://${host}${a.path}` : null;
  const up = (p: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...p } : d));
  useEffect(() => {
    onEditing?.(Boolean(draft));
  }, [draft, onEditing]);
  useEffect(() => () => onEditing?.(false), [onEditing]);

  const onSave = async () => {
    if (!draft) return;
    if (!draft.name.trim()) {
      showInkToast('An author needs a name.');
      return;
    }
    try {
      await save({
        variables: {
          accountId,
          id: a.id,
          input: inputOf(draft),
        },
      });
      // The author comes back as JSON, which the cache can't merge: read it again.
      await refetch();
      setDraft(null);
      onSaved();
      // The author page exists once a published article credits them; before
      // that it's "This page isn't here" (QA round 12).
      showInkToast(
        live
          ? `Saved ${draft.name.trim()}’s profile. It shows on the site now.`
          : `Saved. ${draft.name.trim()}’s page goes live once they’re credited on a published article.`
      );
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t save. Try again.');
    }
  };

  // The server also counts what's live or scheduled; it refuses with the count.
  const credited = Math.max(articles.length, a.articleCount || 0);
  const onDelete = async () => {
    const ok = await askInk({
      title: `Delete ${a.name}?`,
      body: 'No article credits them, so nothing on your site changes.',
      note: 'This can’t be undone. You’d add them again with New author.',
      confirmLabel: 'Delete author',
      danger: true,
    });
    if (!ok) return;
    try {
      await remove({ variables: { accountId, id: a.id } });
      showInkToast(`Deleted ${a.name}.`);
      await onDeleted();
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t delete. Try again.');
    }
  };

  return (
    <section className="au-detail">
      <div className="au-head">
        <Avatar author={draft ? { name: draft.name, photoUrl: draft.photoUrl } : a} size={64} />
        <div className="au-head-text">
          <div className="au-name">{a.name}</div>
          {a.role ? <div className="au-role">{a.role}</div> : null}
          <div className="au-url">
            <span className="ink-mono">{host ? `${host}${a.path}` : a.path}</span>
            {live ? null : <span> · not live yet</span>}
            {a.createdAt ? <span> · {since(a.createdAt)}</span> : null}
          </div>
        </div>
        <div className="au-head-actions">
          {draft ? (
            <>
              <button type="button" className="ink-btn" onClick={() => setDraft(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="ink-btn ink-btn-primary"
                disabled={saving}
                onClick={() => void onSave()}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </>
          ) : (
            <>
              {pageUrl && live ? (
                <a href={pageUrl} target="_blank" rel="noopener noreferrer" className="ink-btn">
                  View page ↗
                </a>
              ) : null}
              <button type="button" className="ink-btn" onClick={() => setDraft(draftOf(a))}>
                Edit details
              </button>
            </>
          )}
        </div>
      </div>

      <div className="au-ready" data-ok={missing ? undefined : 'true'}>
        <div className="au-ready-head">
          <span className="au-ready-dot" />
          {missing
            ? `${missing} ${missing === 1 ? 'detail' : 'details'} missing from this profile`
            : 'Profile complete'}
        </div>
        <p className="au-ready-note">
          {missing
            ? 'Search engines weigh who wrote an article. Fill these in so this byline counts.'
            : 'Every byline links here, and search engines can confirm who this is.'}
        </p>
        <ul className="au-checks">
          {checks.map((c) => (
            <li key={c.label} data-ok={c.ok ? 'true' : undefined}>
              <span aria-hidden="true">{c.ok ? '✓' : '●'}</span>
              <span>
                {c.label}
                {c.ok ? null : <span className="au-check-hint"> {c.hint}</span>}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {draft ? (
        <AuthorFields accountId={accountId} draft={draft} up={up} topicOptions={topicOptions} />
      ) : (
        <>
          {a.bio ||
          a.topics?.length ||
          a.email ||
          SOCIALS.some(([k]) => socialShown(k, a.socials?.[k])) ? (
            <div className="au-section-title">Details</div>
          ) : null}
          <dl className="au-details">
            {/* What's empty is the checklist's to say; only what's filled in shows here. */}
            {a.bio ? (
              <>
                <dt>Bio</dt>
                <dd>{a.bio}</dd>
              </>
            ) : null}
            {a.topics?.length ? (
              <>
                <dt>Writes about</dt>
                <dd>
                  <span className="au-chips">
                    {a.topics.map((t) => (
                      <span key={t} className="au-chip">
                        {t}
                      </span>
                    ))}
                  </span>
                </dd>
              </>
            ) : null}
            {SOCIALS.some(([k]) => socialShown(k, a.socials?.[k])) ? (
              <>
                <dt>Social profiles</dt>
                <dd>
                  <div className="au-socials">
                    {SOCIALS.map(([k, label]) => {
                      const shown = socialShown(k, a.socials?.[k]);
                      return shown ? (
                        <div key={k}>
                          <span className="ink-muted">{label}</span>
                          <span>{shown}</span>
                        </div>
                      ) : null;
                    })}
                  </div>
                </dd>
              </>
            ) : null}
            {a.email ? (
              <>
                <dt>Email</dt>
                <dd>
                  {a.email}
                  <span className="ink-muted"> · Private. Never shown on the site.</span>
                </dd>
              </>
            ) : null}
          </dl>
        </>
      )}

      <div className="au-arts-head">
        <div className="au-section-title">Articles by {a.name}</div>
        <span className="ink-muted">
          {articles.length} {articles.length === 1 ? 'article' : 'articles'} · {live} on the author
          page
        </span>
      </div>
      {articles.length ? (
        <>
          <div className="au-arts">
            {(all ? articles : articles.slice(0, ARTICLES_SHOWN)).map((r) => (
              <button
                key={r.id}
                type="button"
                className="au-art"
                onClick={() => onOpenArticle(r.id)}
              >
                <span className="au-art-title">
                  {r.title}
                  {r.coAuthor ? <span className="au-co"> Co-author</span> : null}
                </span>
                <span className="au-art-status">
                  <span className="ink-dot" style={{ background: STATUS_DOT[bucketOf(r)] }} />
                  {ARTICLE_STATUS[bucketOf(r)]}
                </span>
                <span className="au-art-date">
                  {r.date
                    ? new Date(r.date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })
                    : ''}
                </span>
              </button>
            ))}
          </div>
          {articles.length > ARTICLES_SHOWN ? (
            <button
              type="button"
              className="ink-quiet-link au-more"
              onClick={() => setAll((v) => !v)}
            >
              {all ? 'Show fewer' : `Show all ${articles.length}`}
            </button>
          ) : null}
        </>
      ) : (
        <p className="ink-muted au-none">
          No articles credited yet. Open an article and pick this author in its byline, or ask your
          agent to.
        </p>
      )}
      <p className="au-foot">
        Only published articles appear on the author page. Every byline links here, and search
        engines read this profile from it: name, role, photo, and social profiles as proof of
        identity.{' '}
        {live ? null : <Link to="/content?view=articles">Credit them on an article →</Link>}
      </p>
      {draft ? null : (
        <div className="pg-delete">
          {/* Disabled exactly when the server would refuse, like Delete form. */}
          <button
            type="button"
            className="ink-btn"
            style={{ color: 'var(--ink-red)' }}
            disabled={deleting || credited > 0}
            title={credited ? 'Take them off their bylines first' : undefined}
            onClick={() => void onDelete()}
          >
            {deleting ? 'Deleting…' : 'Delete author'}
          </button>
          {credited ? (
            <span className="pg-note" style={{ margin: 0 }}>
              An author credited on an article can’t be deleted. Take them off those bylines first.
            </span>
          ) : null}
        </div>
      )}
    </section>
  );
}

/** Every detail of an author, for Edit details and New author. */
function AuthorFields({
  accountId,
  draft,
  up,
  topicOptions,
  label = 'Edit author',
}: {
  accountId: string;
  draft: Draft;
  up: (p: Partial<Draft>) => void;
  /** The site's topics (and any already on an author), to pick from. */
  topicOptions: string[];
  label?: string;
}) {
  const [allTopics, setAllTopics] = useState(false);
  // What's picked stays in the list even when the site no longer has it.
  const options = [...new Set([...topicOptions, ...draft.topics])];
  const shown = allTopics
    ? options
    : [
        ...new Set([
          ...options.slice(0, TOPICS_SHOWN),
          ...draft.topics.filter((t) => !options.slice(0, TOPICS_SHOWN).includes(t)),
        ]),
      ];
  const toggleTopic = (t: string) =>
    up({
      topics: draft.topics.includes(t) ? draft.topics.filter((x) => x !== t) : [...draft.topics, t],
    });
  return (
    <div className="au-form" data-screen-label={label}>
      <label className="ink-site-label">
        <span>Name</span>
        <input
          className="ink-site-input"
          value={draft.name}
          maxLength={120}
          placeholder="Name"
          onChange={(e) => up({ name: e.target.value })}
        />
      </label>
      <label className="ink-site-label">
        <span>Role</span>
        <input
          className="ink-site-input"
          value={draft.role}
          maxLength={120}
          placeholder="e.g. College football analyst"
          onChange={(e) => up({ role: e.target.value })}
        />
      </label>
      <label className="ink-site-label">
        <span>
          Bio{' '}
          <span className="ink-muted">
            {draft.bio.length}/{BIO_MAX}
          </span>
        </span>
        <textarea
          className="ink-site-input"
          rows={4}
          maxLength={BIO_MAX}
          value={draft.bio}
          placeholder="Bio"
          aria-describedby="author-bio-help"
          onChange={(e) => up({ bio: e.target.value })}
        />
        <span id="author-bio-help" className="ink-form-hint">
          Two or three sentences: what they cover and why readers should trust them
        </span>
      </label>
      <div className="ink-site-label">
        <span>
          Writes about <span className="ink-muted">· from your site’s topics</span>
        </span>
        {options.length ? (
          <div className="au-chips au-topic-pick" role="group" aria-label="Writes about">
            {shown.map((t) => {
              const on = draft.topics.includes(t);
              return (
                <button
                  key={t}
                  type="button"
                  className="au-chip au-chip-pick"
                  aria-pressed={on}
                  data-on={on ? 'true' : undefined}
                  onClick={() => toggleTopic(t)}
                >
                  {on ? '✓ ' : ''}
                  {t}
                </button>
              );
            })}
            {options.length > shown.length || allTopics ? (
              <button
                type="button"
                className="ink-quiet-link"
                onClick={() => setAllTopics((v) => !v)}
              >
                {allTopics ? 'Show fewer' : `Show all ${options.length}`}
              </button>
            ) : null}
          </div>
        ) : (
          <span className="ink-muted">
            Your articles have no topics yet. Give an article a topic, and it shows here.
          </span>
        )}
      </div>
      <AuthorPhoto
        accountId={accountId}
        url={draft.photoUrl}
        onChange={(url) => up({ photoUrl: url })}
      />
      <div className="ink-site-label">
        <span>
          Social profiles{' '}
          <span className="ink-muted">
            · linked from the author page, so search engines can confirm who this is
          </span>
        </span>
        <div className="au-socials-edit">
          {SOCIALS.map(([k, label, pre]) => (
            <label key={k} className="au-social-input">
              <span>{label}</span>
              <span className="au-social-field">
                <span className="ink-muted ink-mono">{pre}</span>
                <input
                  className="ink-site-input"
                  value={draft.socials[k]}
                  placeholder={SOCIAL_PLACEHOLDER[k]}
                  onChange={(e) => up({ socials: { ...draft.socials, [k]: e.target.value } })}
                />
              </span>
            </label>
          ))}
        </div>
      </div>
      <label className="ink-site-label">
        <span>
          Email <span className="ink-muted">· private, never shown on the site</span>
        </span>
        <input
          className="ink-site-input"
          type="email"
          value={draft.email}
          placeholder="Email"
          onChange={(e) => up({ email: e.target.value })}
        />
      </label>
    </div>
  );
}

/**
 * New author: the same details as Edit details, in the detail pane. Only the
 * name is needed; the page address comes from it and stays put if it changes.
 */
function NewAuthor({
  accountId,
  topicOptions,
  onCancel,
  onAdded,
}: {
  accountId: string;
  topicOptions: string[];
  onCancel: () => void;
  onAdded: (id: string) => void | Promise<void>;
}) {
  const [create, { loading: saving }] = useMutation<{ createTinycmsAuthor: Author }>(
    CREATE_TINYCMS_AUTHOR
  );
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const up = (p: Partial<Draft>) => setDraft((d) => ({ ...d, ...p }));

  const onAdd = async () => {
    const name = draft.name.trim();
    if (!name) {
      showInkToast('An author needs a name.');
      return;
    }
    try {
      const res = await create({ variables: { accountId, input: inputOf(draft) } });
      const added = res.data?.createTinycmsAuthor;
      if (!added) return;
      await onAdded(added.id);
      showInkToast(`Added ${name}. Credit them on an article from its byline.`);
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t save. Try again.');
    }
  };

  return (
    <section className="au-detail" data-screen-label="New author">
      <div className="au-head">
        <Avatar author={{ name: draft.name || '?', photoUrl: draft.photoUrl }} size={64} />
        <div className="au-head-text">
          <div className="au-name">{draft.name.trim() || 'New author'}</div>
          {/* No "No role yet" warning before anything is typed: what's missing
              shows once it's saved (UX review round 11). */}
          <div className="au-new-note">Only the name is needed now. Add the rest any time.</div>
        </div>
        <div className="au-head-actions">
          <button type="button" className="ink-btn" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="ink-btn ink-btn-primary"
            disabled={saving || !draft.name.trim()}
            onClick={() => void onAdd()}
          >
            {saving ? 'Adding…' : 'Add author'}
          </button>
        </div>
      </div>
      <AuthorFields
        accountId={accountId}
        draft={draft}
        up={up}
        topicOptions={topicOptions}
        label="New author form"
      />
    </section>
  );
}

/**
 * An author's photo (UX review round 11): Upload photo first, Choose from
 * Library second — no address to type. An upload goes into Media too.
 */
function AuthorPhoto({
  accountId,
  url,
  onChange,
}: {
  accountId: string;
  url: string;
  onChange: (url: string) => void;
}) {
  const apollo = useApolloClient();
  const file = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [picking, setPicking] = useState(false);
  const [error, setError] = useState('');

  const upload = async (f: File | undefined) => {
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      setError('That isn’t an image. Choose a JPG, PNG, WebP or HEIC photo.');
      return;
    }
    setUploading(true);
    setError('');
    try {
      const { assetId } = await uploadAsset(accountId, f, { filename: f.name });
      const { data } = await apollo.query<{ mediaAsset: LibraryAsset | null }>({
        query: AUTHOR_PHOTO_ASSET,
        variables: { accountId, id: assetId },
        fetchPolicy: 'network-only',
      });
      const got = data?.mediaAsset ? currentUrl(data.mediaAsset) : null;
      if (got) onChange(got);
      else setError('Uploaded, but the photo isn’t ready yet. Choose it from Library in a moment.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The upload didn’t work. Try again.');
    } finally {
      setUploading(false);
      if (file.current) file.current.value = '';
    }
  };

  return (
    <div className="ink-site-label">
      <span>Photo</span>
      <div className="au-photo">
        <input
          ref={file}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => void upload(e.target.files?.[0])}
        />
        <button
          type="button"
          className="ink-btn ink-btn-primary"
          disabled={uploading}
          onClick={() => file.current?.click()}
        >
          {uploading ? 'Uploading…' : url ? 'Replace' : 'Upload photo'}
        </button>
        <button
          type="button"
          className="ink-btn"
          disabled={uploading}
          onClick={() => setPicking(true)}
        >
          Choose from Library
        </button>
        {url ? (
          <button
            type="button"
            className="ink-btn ink-btn-quiet"
            disabled={uploading}
            onClick={() => onChange('')}
          >
            Remove
          </button>
        ) : null}
      </div>
      {error ? (
        <span className="au-photo-error" role="alert">
          {error}
        </span>
      ) : null}
      {picking ? (
        <MediaUrlPicker
          accountId={accountId}
          note="The author’s photo, on their page and beside their byline."
          onPick={(picked) => {
            onChange(picked);
            setError('');
            setPicking(false);
          }}
          onClose={() => setPicking(false)}
        />
      ) : null}
    </div>
  );
}
